import { formatStorageChangePercent } from '~/shared/utils/formatNumber';

import { isVolumeManaged } from './classification';
import { COVERAGE_INSTALL_LABELS, COVERAGE_SUPPORT_LABELS } from './coverage/constants';
import { entityIconSvg, getEntityChipStyle } from './entityIcons';
import { formatBytes, truncate, volumeDisplayName, volumeSubtitle } from './formatBytes';
import { breakdownBarColor } from './sidebar/signalColors';
import { currentInnerPalette } from './sidebar/sidebarPalettes';
import { computeSidebarStorageMetrics } from './sidebar/sidebarStorageMetrics';
import {
  autoscalingDetailLabel,
  autoscalingTooltipIconClass,
} from '../components/sidebar/volumeDetailStatusStyles';
import type { AncestryChip } from './bubble/ancestryChips';
import { sizeArrowSvgHtml } from './bubble/sizeArrowIcon';
import type { ExplorationVolume, HierarchyNode, ViewMode } from '../types/exploration';
import type { CoverageInstance } from './coverage/types';

const HTML_ESCAPE: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(s: string): string {
  return String(s ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPE[ch] ?? ch);
}

export function ttRow(label: string, value: string): string {
  return `<div class="tt-row"><span class="tt-k">${escapeHtml(label)}</span><span class="tt-v">${escapeHtml(value)}</span></div>`;
}

function entityFieldRow(label: string, variant: string, value: string): string {
  const icon = entityIconSvg(variant);
  return (
    `<div class="tt-entity-row">` +
    `<span class="tt-k">${escapeHtml(label)}</span>` +
    `<span class="tt-entity-tag ${variant}">${icon}${escapeHtml(value)}</span>` +
    `</div>`
  );
}

function pathArrowSvg(): string {
  return (
    `<svg class="sc-path-arrow" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">` +
    `<path d="M6 2.5v5M3.5 6.5 6 9 8.5 6.5" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>` +
    `</svg>`
  );
}

function sizeArrowSvg(): string {
  return sizeArrowSvgHtml();
}

function autoscalingIconSvg(): string {
  return (
    `<svg class="vt-autoscale-svg" width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">` +
    `<path d="M8.86816 0C11.1432 0 12.4994 1.35684 12.4932 3.63184V8.86816C12.4932 11.1431 11.1372 12.4999 8.8623 12.5H3.63184C1.35688 12.5 5.14053e-05 11.1435 0 8.8623V3.63184C0 1.35684 1.35684 0 3.63184 0H8.86816ZM6.77734 2.57031C6.5012 2.57031 6.27734 2.79417 6.27734 3.07031C6.27734 3.34645 6.5012 3.57031 6.77734 3.57031H8.21777L3.57031 8.21777V6.77734C3.57031 6.5012 3.34645 6.27734 3.07031 6.27734C2.79417 6.27734 2.57031 6.5012 2.57031 6.77734V9.4248C2.57049 9.7008 2.79428 9.9248 3.07031 9.9248H5.71777C5.99381 9.9248 6.2176 9.7008 6.21777 9.4248C6.21777 9.14866 5.99392 8.9248 5.71777 8.9248H4.27734L8.9248 4.27734V5.71777C8.9248 5.99392 9.14866 6.21777 9.4248 6.21777C9.7008 6.2176 9.9248 5.99381 9.9248 5.71777V3.07031C9.9248 2.79428 9.7008 2.57049 9.4248 2.57031H6.77734Z" fill="currentColor"/>` +
    `</svg>`
  );
}

export function pathSectionHtml(chips: AncestryChip[]): string {
  if (!chips.length) return '';
  return (
    `<div class="sc-path"><div class="sc-path-label">Path</div><div class="sc-path-chips">` +
    chips
      .map((c, i) => {
        const arrow = i > 0 ? `<div class="sc-path-sep">${pathArrowSvg()}</div>` : '';
        return (
          arrow +
          `<div class="sc-path-chip">` +
          `<span class="sc-path-icon ${escapeHtml(c.type)}">${entityIconSvg(c.type)}</span>` +
          `<span class="sc-path-name" title="${escapeHtml(c.name)}">${escapeHtml(truncate(c.name, 28))}</span>` +
          `</div>`
        );
      })
      .join('') +
    `</div></div>`
  );
}

export interface GroupBreakdownContext {
  accountTotalSize: number;
  view?: ViewMode;
  drillPath?: string[];
  ancestry?: AncestryChip[];
}

function volumeStatusItemHtml(label: string, iconHtml: string, value: string): string {
  return (
    `<div class="vt-status-item">` +
    `<div class="vt-status-k">${escapeHtml(label)}</div>` +
    `<div class="vt-status-v">${iconHtml}<span class="vt-status-label">${escapeHtml(value)}</span></div>` +
    `</div>`
  );
}

function volumeChangeBadgeHtml(changePercent: string | null): string {
  if (!changePercent || changePercent === '0%') return '';
  const variant = changePercent.startsWith('-')
    ? 'vt-change-reduction'
    : changePercent.startsWith('+')
      ? 'vt-change-increase'
      : 'vt-change-neutral';
  return `<span class="vt-change-badge ${variant}">${escapeHtml(changePercent)}</span>`;
}

export function groupBreakdownCardHtml(
  node: Pick<HierarchyNode, 'type' | 'name' | 'value' | 'count' | '_volumes' | 'key'>,
  ctx: GroupBreakdownContext
): string {
  const vols = node._volumes ?? [];
  const groupSize = vols.reduce((s, v) => s + (v.size || 0), 0) || node.value || 0;
  const view = ctx.view ?? 'exploration';
  const palette = currentInnerPalette(view);
  const signalKey = palette.order[0];
  const metrics = computeSidebarStorageMetrics(vols, view);

  const chip = getEntityChipStyle(node.type || '');
  const icon = entityIconSvg(node.type || '');
  const safeName = truncate(String(node.name || ''), 40);

  const buckets = new Map<string, { label: string; size: number; count: number }>();
  vols.forEach((v) => {
    const key = (v[palette.field] as string) || '(unknown)';
    if (!buckets.has(key)) {
      buckets.set(key, { label: palette.label(key), size: 0, count: 0 });
    }
    const b = buckets.get(key)!;
    b.size += v.size || 0;
    b.count += 1;
  });

  const rows = [...buckets.entries()]
    .map(([key, b]) => ({ key, ...b }))
    .filter((b) => b.size > 0)
    .sort((a, b) => b.size - a.size)
    .slice(0, 4);

  const total = groupSize || 1;
  const rowHtml = rows
    .map((r) => {
      const pct = Math.round((r.size / total) * 100);
      const barColor = breakdownBarColor(r.key, {
        signalKey,
        colorForKey: palette.color,
        colorAllKeys: view === 'activation',
      });
      return (
        `<div class="gc-row">` +
        `<span class="gc-row-label" title="${escapeHtml(r.label)}">${escapeHtml(r.label)}</span>` +
        `<span class="gc-row-size">${escapeHtml(formatBytes(r.size))}</span>` +
        `<span class="gc-row-count">(${r.count.toLocaleString()})</span>` +
        `<span class="gc-row-bar"><span class="gc-row-fill" style="width:${Math.min(100, pct)}%; background:${barColor};"></span></span>` +
        `<span class="gc-row-pct">${pct}%</span>` +
        `</div>`
      );
    })
    .join('');

  const metricsHtml = metrics
    .map(
      (metric) =>
        `<div class="thc-stat"><div class="thc-stat-k">${escapeHtml(metric.title)}</div>` +
        `<div class="thc-stat-row"><span class="thc-stat-v">${escapeHtml(formatBytes(metric.size))}</span>` +
        `<span class="thc-stat-badge">${metric.count.toLocaleString()} Vol.</span></div></div>`
    )
    .join('');

  return (
    `<div class="thc-header">` +
    `<div class="thc-title" title="${escapeHtml(safeName)}">${escapeHtml(safeName)}</div>` +
    `<div class="thc-badge" style="background:${chip.bg}; color:${chip.fg}">` +
    `<span class="thc-badge-icon">${icon}</span>${escapeHtml(chip.label)}` +
    `</div></div>` +
    `<div class="thc-stats">${metricsHtml}</div>` +
    `<div class="gc-rows">${rowHtml}</div>`
  );
}

function statusTooltipDotHtml(key: string): string {
  if (key === 'active') {
    return `<span class="vt-autoscale-icon vt-autoscale-active" aria-hidden="true">${autoscalingIconSvg()}</span>`;
  }
  const dotClass =
    key === 'supported'
      ? 'vt-dot-supported'
      : key === 'unattached'
        ? 'vt-dot-unattached'
        : 'vt-dot-unsupported';
  return `<span class="vt-dot ${dotClass}" aria-hidden="true"></span>`;
}

export function aggregateVolumeSizes(vols: ExplorationVolume[]): {
  originalGiB: number;
  currentGiB: number;
} {
  let originalGiB = 0;
  let currentGiB = 0;
  for (const v of vols) {
    originalGiB += v.size || 0;
    const managed = isVolumeManaged(v);
    currentGiB += managed && v.datafyDiskSize != null ? v.datafyDiskSize : v.size || 0;
  }
  return { originalGiB, currentGiB };
}

function volumeSizeStatHtml(label: string, value: string, badgeHtml = ''): string {
  return (
    `<div class="thc-stat"><div class="thc-stat-k">${escapeHtml(label)}</div>` +
    `<div class="thc-stat-row"><span class="thc-stat-v">${escapeHtml(value)}</span>${badgeHtml}</div></div>`
  );
}

export function statusBreakdownCardHtml(
  node: Pick<HierarchyNode, 'type' | 'name' | 'value' | 'count' | '_volumes' | 'key'>,
  ctx: GroupBreakdownContext
): string {
  const vols = node._volumes ?? [];
  const count = vols.length || node.count || 0;
  const key = node.key || '';
  const { originalGiB, currentGiB } = aggregateVolumeSizes(vols);
  const size = originalGiB || node.value || 0;
  const currentSize = currentGiB || size;
  const changePercent = formatStorageChangePercent(originalGiB, currentGiB);
  const showRange = currentSize !== size;

  const safeName = truncate(String(node.name || ''), 32);
  const pathHtml = pathSectionHtml(ctx.ancestry ?? []);

  const countBadgeHtml = `<span class="thc-stat-badge">${count.toLocaleString()} Vol.</span>`;
  const sizesHtml = showRange
    ? volumeSizeStatHtml('Size', formatBytes(size), countBadgeHtml) +
    `<div class="vt-size-arrow">${sizeArrowSvg()}</div>` +
    volumeSizeStatHtml('Current Size', formatBytes(currentSize), volumeChangeBadgeHtml(changePercent))
    : volumeSizeStatHtml('Size', formatBytes(size), countBadgeHtml);

  return (
    `<div class="st-card">` +
    `<div class="st-header">` +
    statusTooltipDotHtml(key) +
    `<span class="vt-status-label" title="${escapeHtml(safeName)}">${escapeHtml(safeName)}</span>` +
    `</div>` +
    `<div class="vt-sizes">` +
    sizesHtml +
    `</div>` +
    pathHtml +
    `</div>`
  );
}

export function volumeTooltipHtml(v: ExplorationVolume, ancestry: AncestryChip[] = []): string {
  const title = volumeDisplayName(v);
  const subtitle = volumeSubtitle(v);
  const managed = isVolumeManaged(v);

  const autoscaleIcon = `<span class="vt-autoscale-icon ${autoscalingTooltipIconClass(v)}" aria-hidden="true">${autoscalingIconSvg()}</span>`;

  const currentGiB = managed && v.datafyDiskSize != null ? v.datafyDiskSize : v.size;

  let sizesHtml: string;
  if (managed && currentGiB !== v.size) {
    const changePercent = formatStorageChangePercent(v.size, currentGiB);
    sizesHtml =
      `<div class="vt-sizes">` +
      volumeSizeStatHtml('Original Size', formatBytes(v.size)) +
      `<div class="vt-size-arrow">${sizeArrowSvg()}</div>` +
      volumeSizeStatHtml('Current Size', formatBytes(currentGiB), volumeChangeBadgeHtml(changePercent)) +
      `</div>`;
  } else {
    sizesHtml =
      `<div class="vt-sizes">` + volumeSizeStatHtml('Size', formatBytes(v.size)) + `</div>`;
  }

  return (
    `<div class="vt-card">` +
    `<div class="vt-header">` +
    `<div class="vt-title" title="${escapeHtml(title)}">${escapeHtml(title)}</div>` +
    `<div class="vt-subtitle">${escapeHtml(subtitle)}</div>` +
    `</div>` +
    sizesHtml +
    `<div class="vt-status-grid">` +
    volumeStatusItemHtml('Autoscaling', autoscaleIcon, autoscalingDetailLabel(v.activationStatus)) +
    `</div>` +
    pathSectionHtml(ancestry) +
    `</div>`
  );
}

export function smallVolumesTooltipHtml(
  shown: ExplorationVolume[],
  smallTotal: number,
  totalCount: number
): string {
  const n = totalCount;
  let html =
    `<div class="tt-title">${n} small volume${n === 1 ? '' : 's'} (&lt; 50 GiB)</div>` +
    ttRow('Total', formatBytes(smallTotal));
  shown.forEach((v) => {
    html += ttRow(truncate(volumeDisplayName(v), 22), formatBytes(v.size));
  });
  const overflow = totalCount - shown.length;
  if (overflow > 0) {
    html += ttRow(`… and ${overflow} more`, '');
  }
  return html;
}

export function coverageTooltipHtml(inst: CoverageInstance): string {
  const instanceIcon = entityIconSvg('instance');
  return (
    `<div class="tt-title">${instanceIcon}${escapeHtml(inst.instanceId)}</div>` +
    ttRow('Install', COVERAGE_INSTALL_LABELS[inst.install]) +
    ttRow('Support', COVERAGE_SUPPORT_LABELS[inst.support]) +
    (inst.cluster ? entityFieldRow('Cluster', 'cluster', inst.cluster) : '') +
    (inst.nodeGroup ? entityFieldRow('Node Group', 'nodeGroup', inst.nodeGroup) : '') +
    (inst.region ? ttRow('Region', inst.region) : '') +
    ttRow('Volumes', `${inst.totalCount} (${inst.supportedCount} supported)`) +
    ttRow(
      'Storage',
      `${formatBytes(inst.totalSize)} (${formatBytes(inst.supportedSize)} supported)`
    )
  );
}

function volumesUnderOverflowChild(child: HierarchyNode): ExplorationVolume[] {
  if (child._volumes) {
    return child._volumes;
  }
  if (child.type === 'volume' && child.volume) {
    return [child.volume];
  }
  return [];
}

/**
 * The entity type a "+N More" bubble stands in for. `applyBubbleCaps` only ever
 * caps siblings of one parent, so the hidden set is homogeneous in practice —
 * anything else falls back to the generic overflow chip.
 */
function overflowChildType(children: HierarchyNode[] | undefined): HierarchyNode['type'] {
  const types = new Set((children ?? []).map((c) => c.type));
  return types.size === 1 ? [...types][0]! : 'overflow';
}

export function bubbleNodeTooltipHtml(
  node: Pick<
    HierarchyNode,
    | 'type'
    | 'name'
    | 'volume'
    | 'count'
    | 'value'
    | '_isOverflow'
    | '_hidden'
    | '_volumes'
    | '_overflowChildren'
    | 'key'
  >,
  ctx: GroupBreakdownContext
): string {
  if (node.type === 'volume' && node.volume) {
    return volumeTooltipHtml(node.volume, ctx.ancestry);
  }
  if (node._isOverflow) {
    // Same tip-card design as any other group bubble, but scoped to only the
    // hidden children this "+N More" bubble represents — not the parent group.
    const hiddenVolumes = (node._overflowChildren ?? []).flatMap(volumesUnderOverflowChild);
    return groupBreakdownCardHtml(
      {
        // Badge the card with what is actually hidden (Instance / Volume / …),
        // not the synthetic "Overflow" type of the bubble itself.
        type: overflowChildType(node._overflowChildren),
        name: node.name,
        value: node.value,
        _volumes: hiddenVolumes,
      },
      ctx
    );
  }
  if (node.type === 'status' || node.type === 'reason') {
    return statusBreakdownCardHtml(node, ctx);
  }
  return groupBreakdownCardHtml(node, ctx);
}
